import React, { Fragment, useState } from 'react';
import { bool, func, string, shape, number } from 'prop-types';

import cn from 'classnames';
import Modal from 'ps-components/Modal/Modal';
import ModalHeader from 'ps-components/Modal/ModalHeader/ModalHeader';
import ModalBody from 'ps-components/Modal/ModalBody/ModalBody';
import ModalFooter from 'ps-components/Modal/ModalFooter/ModalFooter';
import { Toggle } from 'ps-design';

import './DefaultWeightModal.scss';

function getWeightingLabel(code) {
  const mapping = {
    one: 'Equal Weighting (1)',
    companies: 'Weight by # of Companies',
    employees: 'Weight by # of Employees',
    zero: '0 Weighting'
  };

  return mapping[code] || 'Equal Weighting (1)';
}

DefaultWeightModal.propTypes = {
  show: bool,
  onClose: func,
  weighting: string,
  applyWeighting: func,
  applyWeightingReducer: shape({
    loading: bool,
    countWithZero: number,
    countWithoutZero: number,
    error: string,
    loaded: bool
  })
};

function DefaultWeightModal(props) {
  const [zeroWeightToggle, setZeroWeightToggle] = useState(false);

  const { onClose, applyWeighting, weighting, show, applyWeightingReducer } = props;
  const { loading: isApplyLoading, countWithZero, countWithoutZero, loaded } = applyWeightingReducer;

  const hasCounts = countWithZero != null && countWithoutZero != null;
  const currentCount = zeroWeightToggle ? countWithZero : countWithoutZero;
  const countText = currentCount === 1 ? 'match' : 'matches';

  function closeModal() {
    onClose();
  }

  function toggleZeroWeight() {
    setZeroWeightToggle(!zeroWeightToggle);
  }

  function applyWeightingButtonAction() {
    applyWeighting(weighting, zeroWeightToggle);
  }

  return (
    <Modal className="default-weighting-modal" allowClose displayCloseButton show={show} onClose={closeModal}>
      <ModalHeader>
        <h2 className="pxl-header-sm">Apply Weighting Methodology</h2>
      </ModalHeader>

      <ModalBody>
        <div className="default-weighting-modal__contents">
          <div className="default-weighting-modal__selected pxl-text-body">
            Selected Weighting:
            <b>{` ${getWeightingLabel(weighting || 'one')}`}</b>
          </div>

          {loaded ? (
            <div className="default-weighting-modal__description pxl-text-body">
              {!hasCounts || currentCount === 0 ? (
                'No applicable matches were found for reweighting'
              ) : (
                <Fragment>
                  <p>{`Reweighting has started for ${currentCount} pricing ${countText}.`}</p>
                  <p>You will receive an email confirmation once reweighting is finished.</p>
                </Fragment>
              )}
            </div>
          ) : (
            <Fragment>
              <div className="default-weighting-modal__description pxl-text-body">
                {currentCount === 0 ? (
                  <Fragment>
                    <b>0</b> pricing matches found for reweighting
                  </Fragment>
                ) : (
                  <Fragment>
                    <b>{`${currentCount == null ? 'All' : currentCount}`}</b> pricing matches will be reweighted in your
                    organization for the current pricing year, across all groups{' '}
                    <b>{`${zeroWeightToggle ? '(including' : '(excluding'}
                      zero-weighted matches)`}</b>
                    . This cannot be undone. Are you sure you want to continue?
                  </Fragment>
                )}
              </div>

              <div className="default-weighting-modal__toggle-container">
                <Toggle
                  onChange={toggleZeroWeight}
                  id="default-weighting-modal__toggle"
                  className="default-weighting-modal__toggle"
                  value={zeroWeightToggle}
                />
                <span className={cn('default-weighting-modal__text', { light: !zeroWeightToggle })}>
                  Apply weighting to zero-weighted matches
                </span>
              </div>

              <div className="default-weighting-modal__note pxl-text-body">
                Note: To set this weighting as the site default, please click Save in configuration settings
              </div>
            </Fragment>
          )}
        </div>
      </ModalBody>

      <ModalFooter
        cancelBtnClick={closeModal}
        cancelBtnTxtName={loaded ? 'Close' : 'Cancel'}
        submitBtnClick={applyWeightingButtonAction}
        submitBtnTxtName={isApplyLoading ? <i className="icon-animate-spin icon-spin" /> : 'Apply Weighting'}
        hideSubmitBtn={loaded}
        saveButtonDisabled={!hasCounts || currentCount === 0}
      />
    </Modal>
  );
}

export default DefaultWeightModal;
